import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { useOrganizerFollowMutation } from './useOrganizerFollowMutation';
import { isGDPRRegime, fetchGDPRCommSettings, hasChosenGDPR } from './gdpr-utils';
import type { FollowNotificationType } from '../useFollowContext';

export const AUTO_FOLLOW_PARAM = 'autoFollow';

interface UseAutoFollowParams {
  organizerId: string;
  authenticated: boolean;
  followedByUser: boolean;
  statusLoading: boolean;
  userId: string | undefined;
  setFollowedByUser: Dispatch<SetStateAction<boolean>>;
  setNumFollowers: Dispatch<SetStateAction<number | undefined>>;
  setMutationLoading: Dispatch<SetStateAction<boolean>>;
  setShouldShowGDPRModal: Dispatch<SetStateAction<boolean>>;
  showNotification: (type: FollowNotificationType) => void;
}

function hasAutoFollowParam(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get(AUTO_FOLLOW_PARAM) === '1';
}

function clearAutoFollowParam(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete(AUTO_FOLLOW_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}

export function useAutoFollow({
  organizerId,
  authenticated,
  followedByUser,
  statusLoading,
  userId,
  setFollowedByUser,
  setNumFollowers,
  setMutationLoading,
  setShouldShowGDPRModal,
  showNotification,
}: UseAutoFollowParams): void {
  const attempted = useRef(false);
  const followMutation = useOrganizerFollowMutation();

  useEffect(() => {
    if (attempted.current || statusLoading) return;
    if (!hasAutoFollowParam()) return;

    attempted.current = true;

    if (!authenticated || followedByUser) {
      clearAutoFollowParam();
      return;
    }

    let cancelled = false;

    (async () => {
      setMutationLoading(true);
      try {
        const { actionPerformed, numFollowers: updatedFollowers } =
          await followMutation.mutateAsync(organizerId);

        if (cancelled) return;

        if (actionPerformed) {
          setFollowedByUser(true);
          if (typeof updatedFollowers === 'number') {
            setNumFollowers(updatedFollowers);
          } else {
            setNumFollowers((prev) => (typeof prev === 'number' ? prev + 1 : prev));
          }
          showNotification('success');

          if (userId && isGDPRRegime()) {
            try {
              const commSettings = await fetchGDPRCommSettings(userId);
              if (!hasChosenGDPR(commSettings)) {
                setShouldShowGDPRModal(true);
              }
            } catch {
              setShouldShowGDPRModal(true);
            }
          }
        }
      } catch {
        if (!cancelled) {
          showNotification('follow-error');
        }
      } finally {
        if (!cancelled) {
          setMutationLoading(false);
          clearAutoFollowParam();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [statusLoading, authenticated, followedByUser, organizerId]);
}
